import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SlidersHorizontal, Tags, History, X, Plus, Sparkles, Target } from "lucide-react";
import DataTable from "@/components/DataTable";
import { TRACKERS } from "@/config/trackers";
import { toast } from "sonner";

const AI_MODELS = [
  { label: "Claude Sonnet 4.6", provider: "anthropic", model: "claude-sonnet-4-6" },
  { label: "GPT-5.4", provider: "openai", model: "gpt-5.4" },
  { label: "Gemini 3.1 Pro", provider: "gemini", model: "gemini-3.1-pro-preview" },
];

function ListEditor({ label, items, onChange, testid }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <Label className="text-slatetext text-xs mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {(items || []).map((it) => (
          <span key={it} className="flex items-center gap-1.5 text-sm bg-navy border border-surface-hover rounded-md px-2.5 py-1">
            {it}
            <button data-testid={`${testid}-remove-${it}`} onClick={() => onChange(items.filter((x) => x !== it))} className="text-slatetext hover:text-[#DC2626] transition-colors duration-200"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={`Add ${label.toLowerCase()}…`} className="bg-navy border-surface-hover text-white focus:border-gold" />
        <Button data-testid={`${testid}-add`} onClick={() => { if (val && !items.includes(val)) { onChange([...items, val]); setVal(""); } }} className="bg-surface-hover hover:bg-gold hover:text-navy"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: log = [] } = useQuery({ queryKey: ["change-log"], queryFn: async () => (await api.get("/change-log")).data });
  const [s, setS] = useState(null);

  useEffect(() => { if (settings) setS(settings); }, [settings]);

  const saveMut = useMutation({
    mutationFn: async (payload) => api.put("/settings", payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Settings saved"); },
  });

  if (!s) return <div className="text-slatetext">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div>
      <PageHeader title="Settings" subtitle="Customize thresholds, taxonomy and review the change log">
        <Button data-testid="save-settings" onClick={() => saveMut.mutate(s)} className="bg-gold text-navy hover:bg-gold-hover">Save Changes</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-lg border border-surface-hover bg-surface p-6">
          <div className="flex items-center gap-2 mb-5"><SlidersHorizontal className="w-5 h-5 text-gold" /><h3 className="font-heading font-bold">Threshold Configuration</h3></div>
          <div className="space-y-4">
            <Field label="Viral views threshold" value={s.viral_views_threshold} onChange={(v) => set("viral_views_threshold", Number(v))} testid="viral-views" />
            <Field label="Viral engagement threshold" value={s.viral_engagement_threshold} onChange={(v) => set("viral_engagement_threshold", Number(v))} testid="viral-eng" />
            <Field label="ROI alert threshold (%)" value={s.roi_alert_threshold} onChange={(v) => set("roi_alert_threshold", Number(v))} testid="roi-alert" />
            <Field label="Follower growth target (%)" value={s.follower_growth_target} onChange={(v) => set("follower_growth_target", Number(v))} testid="follower-target" />
          </div>
        </section>

        <section className="rounded-lg border border-surface-hover bg-surface p-6">
          <div className="flex items-center gap-2 mb-5"><Sparkles className="w-5 h-5 text-gold" /><h3 className="font-heading font-bold">AI Model</h3></div>
          <Label className="text-slatetext text-xs mb-2 block">Model powering the intelligence panel</Label>
          <select
            data-testid="ai-model-select"
            value={s.ai_model}
            onChange={(e) => { const m = AI_MODELS.find((x) => x.model === e.target.value); set("ai_model", m.model); setS((prev) => ({ ...prev, ai_model: m.model, ai_provider: m.provider })); }}
            className="w-full bg-navy border border-surface-hover rounded-md px-3 py-2 text-sm focus:border-gold outline-none"
          >
            {AI_MODELS.map((m) => <option key={m.model} value={m.model}>{m.label}</option>)}
          </select>
          <p className="text-xs text-slatetext mt-3">Powered by the Emergent Universal LLM key. Switch anytime without new credentials.</p>
        </section>

        <section className="rounded-lg border border-surface-hover bg-surface p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5"><Tags className="w-5 h-5 text-gold" /><h3 className="font-heading font-bold">Content Type & Platform Management</h3></div>
          <div className="grid md:grid-cols-2 gap-6">
            <ListEditor label="Content Types" items={s.content_types} onChange={(v) => set("content_types", v)} testid="content-types" />
            <ListEditor label="Platforms" items={s.platforms} onChange={(v) => set("platforms", v)} testid="platforms" />
            <ListEditor label="Audience Segments" items={s.audience_segments} onChange={(v) => set("audience_segments", v)} testid="audience-segments" />
            <ListEditor label="Design Types" items={s.design_types} onChange={(v) => set("design_types", v)} testid="design-types" />
            <ListEditor label="Businesses" items={s.businesses} onChange={(v) => set("businesses", v)} testid="businesses" />
            <ListEditor label="Branches" items={s.branches} onChange={(v) => set("branches", v)} testid="branches" />
            <ListEditor label="Departments" items={s.departments} onChange={(v) => set("departments", v)} testid="departments" />
            <ListEditor label="Roles" items={s.roles} onChange={(v) => set("roles", v)} testid="roles" />
          </div>
        </section>

        <section className="rounded-lg border border-surface-hover bg-surface p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5"><Target className="w-5 h-5 text-gold" /><h3 className="font-heading font-bold">KPI Registry (config-driven)</h3><span className="text-xs text-slatetext ml-2">add / edit / deactivate KPIs — target, unit, weightage, higher/lower-is-better. Never alters historical data.</span></div>
          <DataTable title="KPIs" endpoint="kpi-configs" globalFilter={false} columns={TRACKERS["kpi-configs"].columns} />
        </section>

        <section className="rounded-lg border border-surface-hover bg-surface lg:col-span-2">
          <div className="flex items-center gap-2 p-6 pb-4"><History className="w-5 h-5 text-gold" /><h3 className="font-heading font-bold">Data Change Log</h3><span className="text-xs text-slatetext ml-2">who changed what, when</span></div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface"><tr className="text-slatetext text-xs uppercase border-b border-surface-hover">
                <th className="px-4 py-3 text-left">When</th><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Action</th><th className="px-4 py-3 text-left">Entity</th>
              </tr></thead>
              <tbody>
                {log.map((l) => (
                  <tr key={l.id} className="border-b border-surface-hover/60">
                    <td className="px-4 py-2.5 text-slatetext whitespace-nowrap">{new Date(l.timestamp).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2.5">{l.user_name || l.user_email}</td>
                    <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${l.action === "delete" ? "rag-red" : l.action === "create" ? "rag-green" : "rag-amber"}`}>{l.action}</span></td>
                    <td className="px-4 py-2.5 text-slatetext">{l.entity}</td>
                  </tr>
                ))}
                {log.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slatetext">No changes logged yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-slatetext text-xs mb-1.5 block">{label}</Label>
      <Input data-testid={`setting-${testid}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-navy border-surface-hover text-white focus:border-gold" />
    </div>
  );
}
